/**
 * CR425: SpendBoxBlock (V2) — Deterministic, deal-type-aware
 *
 * Uses getSpendBoxDisplay() SSOT resolver for all deal types.
 * Admin previews show "Spend data missing" placeholder with tooltip when enabled but empty.
 * Public view hides the block when empty.
 */

import type { FC } from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";
import { getSpendBoxDisplay } from "@/lib/deal-cards/displayValueResolver";
import { formatCurrency } from "@/lib/deal-cards/bonusValueModel";
import { DollarSign, AlertCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Deal-type color map
const COLOR_MAP: Record<string, { bg: string; border: string; icon: string; text: string; textBold: string }> = {
  BONUS:    { bg: "bg-muted/40", border: "border-border/50", icon: "text-muted-foreground", text: "text-muted-foreground", textBold: "text-foreground" },
  DISCOUNT: { bg: "bg-orange-50/50", border: "border-orange-200/50", icon: "text-orange-500", text: "text-orange-700", textBold: "text-orange-800" },
  POINTS:   { bg: "bg-purple-50/50", border: "border-purple-200/50", icon: "text-purple-500", text: "text-purple-700", textBold: "text-purple-800" },
  IN_KIND:  { bg: "bg-amber-50/50", border: "border-amber-200/50", icon: "text-amber-500", text: "text-amber-700", textBold: "text-amber-800" },
  DEFAULT:  { bg: "bg-muted/40", border: "border-border/50", icon: "text-muted-foreground", text: "text-muted-foreground", textBold: "text-foreground" },
};

export const SpendBoxBlock: FC<DealCardBlockProps<Deal>> = ({ deal, block, context }) => {
  if (!block.enabled) return null;

  const { category } = getDealType(deal);
  const display = getSpendBoxDisplay(deal);
  const colors = COLOR_MAP[category] ?? COLOR_MAP.DEFAULT;

  // CR450: Canonical label key with legacy fallback
  const spendTitle = context.labels?.qualifying_spend_box_title
    ?? context.labels?.qualifying_spend_title
    ?? display.label;

  // --- Empty: admin placeholder or hide ---
  if (display.mode === "empty") {
    if (context.isAdmin) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-md border border-dashed border-muted-foreground/30"
                data-block="spend_box"
                data-block-id={block.id}
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground/50" />
                  <span className="text-sm text-muted-foreground/60 italic">
                    Spend data missing
                  </span>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              <p className="font-semibold">Why hidden?</p>
              <p className="mt-1">{display.reason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return null;
  }

  // --- Currency display ---
  return (
    <div
      className={`flex items-center justify-between py-2 px-3 ${colors.bg} rounded-md border ${colors.border}`}
      data-block="spend_box"
      data-block-id={block.id}
    >
      <div className="flex items-center gap-2">
        <DollarSign className={`h-4 w-4 ${colors.icon}`} />
        <span className={`text-sm font-medium ${colors.text}`}>{spendTitle}</span>
      </div>
      <span className={`text-lg font-semibold ${colors.textBold}`}>
        {formatCurrency(display.value)}
      </span>
    </div>
  );
};

export default SpendBoxBlock;
